import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useTypedSearchParams } from "react-router-typesafe-routes";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { useDisplayLocale } from "~/common/functions/useDisplayLocale";
import { useEvidenceActivation, useEvidenceDetail, useEvidenceVersionOwner } from "~/features/evidences/hooks";
import type { EvidenceDetailTab } from "~/features/evidences/types";
import { toEvidenceDetailUI } from "~/features/evidences/utils/transformers";
import { v2Route } from "~/v2/routes";
import { EvidenceActivationDialog } from "./EvidenceActivationDialog";
import { EvidenceDetailPresenter } from "./EvidenceDetailPresenter";

interface EvidenceDetailProps {
  id: string;
  onBack: () => void;
  variant?: "page" | "peek";
}

export function EvidenceDetail({ id, onBack, variant = "page" }: EvidenceDetailProps) {
  const { t } = useTranslation();
  const locale = useDisplayLocale();
  const [params, setParams] = useTypedSearchParams(v2Route.evidences);
  const navigate = useNavigate();
  const { data, refetch } = useEvidenceDetail(id);
  const { onActivate, onDeactivate, processing } = useEvidenceActivation();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const activeTab: EvidenceDetailTab = params["tab-evidenceDetail"] ?? "data";
  const onTabChange = useCallback(
    (next: EvidenceDetailTab) => {
      setParams((prev) => ({ ...prev, "tab-evidenceDetail": next === "data" ? undefined : next }));
    },
    [setParams],
  );

  const raw = data.evidences[0];
  const versionOwner = useEvidenceVersionOwner(id, raw != null);

  useEffect(() => {
    if (!versionOwner) return;
    const selection = { "tab-evidenceDetail": "lineage", selectedEvidenceVersion: versionOwner.id } as const;
    // Replaced, not pushed, so Back does not return to the ID that only names a version.
    if (variant === "peek") {
      setParams((prev) => ({ ...prev, ...selection, selectedEvidence: versionOwner.evidenceId }), { replace: true });
    } else {
      navigate(
        v2Route.evidences.detail.$buildPath({
          params: { id: versionOwner.evidenceId },
          searchParams: { ...params, ...selection },
        }),
        { replace: true },
      );
    }
  }, [versionOwner, variant, setParams, navigate, params]);
  const detail = useMemo(() => (raw ? toEvidenceDetailUI(raw, locale) : null), [raw, locale]);

  const handleCopyId = useCallback(
    async (value: string) => {
      try {
        await copyToClipboard(value);
        toast.success(t("feature.common.idCopied"));
      } catch {
        toast.error(t("feature.common.copyToClipboardFailed"));
      }
    },
    [t],
  );

  const handleConfirm = useCallback(async () => {
    if (!raw) return;
    const ok = await (raw.deactivatedAt ? onActivate(raw.id) : onDeactivate(raw.id));
    setConfirmOpen(false);
    if (ok) void refetch();
  }, [raw, onActivate, onDeactivate, refetch]);

  if (versionOwner) return null;
  if (!raw || !detail) {
    return <div className="mx-auto max-w-[var(--content-max)] p-8 text-[13px] text-fg-3">{t("ui.notFound")}</div>;
  }

  return (
    <>
      <EvidenceDetailPresenter
        detail={detail}
        onBack={onBack}
        onCopyId={handleCopyId}
        onRequestToggleActivation={() => setConfirmOpen(true)}
        activeTab={activeTab}
        onTabChange={onTabChange}
        selectedVersionId={params.selectedEvidenceVersion ?? null}
        variant={variant}
      />
      <EvidenceActivationDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        deactivate={!detail.isDeactivated}
        processing={processing}
        onConfirm={handleConfirm}
      />
    </>
  );
}
